/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ConfigENV } from '@/init';
import { User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import { AuthService } from './types';

/**
 * GitLab OIDC client built on top of `oidc-client-ts`. It uses the standard
 * Authorization Code flow with PKCE and lets the library handle token storage,
 * silent renewal and the redirect callback.
 */
export class OIDCServiceGitlab extends AuthService {
  private readonly userManager: UserManager;
  private user: User | null = null;
  private refreshPromise: Promise<void> | null = null;

  constructor(config: NonNullable<ConfigENV['SERVER_CONFIG']['oidc']>) {
    super();
    const issuer = config.issuer.endsWith('/') ? config.issuer.slice(0, -1) : config.issuer;
    const settings: UserManagerSettings = {
      authority: issuer,
      metadata: {
        issuer,
        authorization_endpoint: `${issuer}/oauth/authorize`,
        token_endpoint: `${issuer}/oauth/token`,
        userinfo_endpoint: `${issuer}/oauth/userinfo`,
        revocation_endpoint: `${issuer}/oauth/revoke`,
        jwks_uri: `${issuer}/oauth/discovery/keys`
      },
      client_id: config.clientId,
      redirect_uri: `${window.location.origin}${window.location.pathname}`,
      response_type: 'code',
      scope: 'openid profile email read_api',
      automaticSilentRenew: false,
      userStore: new WebStorageStateStore({ store: window.localStorage })
    };
    this.userManager = new UserManager(settings);
  }

  useWithCredentials(): boolean {
    return false;
  }

  /**
   * Prevent accidental serialization of the underlying `UserManager`, which
   * holds circular references (e.g. via its silent-renew service). React Query
   * hashes query keys with `JSON.stringify`, and some keys transitively include
   * this service through the API endpoint instance.
   */
  toJSON(): string {
    return `OIDCServiceGitlab(${this.userManager.settings.client_id})`;
  }

  getToken(): string | null {
    return this.user?.access_token ?? null;
  }

  async initialize(): Promise<boolean> {
    const params = new URLSearchParams(window.location.search);
    if (params.has('code') && params.has('state')) {
      const user = await this.userManager.signinRedirectCallback();
      this.user = user;
      const redirect = (user.state as string | undefined) ?? this.userManager.settings.redirect_uri;
      window.location.replace(redirect);
      return true;
    }

    this.user = await this.userManager.getUser();

    return this.user !== null && !this.user.expired;
  }

  async login(redirect?: string): Promise<void> {
    await this.userManager.signinRedirect({ state: redirect ?? window.location.href });
  }

  async logout(redirect?: string): Promise<void> {
    await this.userManager.removeUser();
    this.user = null;
    if (redirect) {
      window.location.assign(redirect);
    }
    this.clear();
  }

  isExpired(): boolean {
    if (!this.user) {
      return true;
    }
    if (this.user.expired === undefined) {
      return true;
    }
    return this.user.expired;
  }

  async refreshToken(): Promise<void> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.userManager
      .signinSilent()
      .then((user) => {
        this.user = user;
      })
      .finally(() => {
        this.refreshPromise = null;
      });
    return this.refreshPromise;
  }
}
